"use strict";
/**
 * API Client for skylab-api
 *
 * Provides a secure, type-safe HTTP client with:
 * - Authorization header injection
 * - Automatic token refresh on 401
 * - Request/response interceptors
 * - Retry logic with exponential backoff
 * - Request timeout
 * - Development logging
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ApiClient = exports.apiClient = void 0;
const api_1 = require("../config/api");
/**
 * API Client class
 */
class ApiClient {
    constructor() {
        this.interceptors = [];
        this.getToken = null;
        this.refreshToken = null;
        /**
         * Page-scoped token override. When set, this token takes priority over the
         * normal token getter for ALL requests. Intended exclusively for the Salesforce
         * iframe page, which sets/clears this via a useEffect with cleanup so it never
         * bleeds into other routes. Must NOT be used to bypass standard auth elsewhere.
         */
        this.tokenOverride = null;
    }
    /**
     * Set token getter function (called on each request)
     */
    setTokenGetter(getter) {
        this.getToken = getter;
    }
    /**
     * Set a page-scoped token override that takes priority over the normal token getter.
     * Pass null to clear. Only use this from StudentPlanIframePage (via useEffect with
     * cleanup) so the override is always cleared when the page unmounts.
     */
    setTokenOverride(token) {
        this.tokenOverride = token;
    }
    /**
     * Set token refresh function (called on 401)
     */
    setTokenRefresher(refresher) {
        this.refreshToken = refresher;
    }
    /**
     * Add request/response interceptor
     */
    addInterceptor(interceptor) {
        this.interceptors.push(interceptor);
    }
    /**
     * Build full URL with query parameters
     */
    buildUrl(endpoint, params) {
        const url = `${api_1.API_CONFIG.BASE_URL}${endpoint}`;
        if (!params || Object.keys(params).length === 0) {
            return url;
        }
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            // URLSearchParams.append() handles encoding automatically
            searchParams.append(key, String(value));
        });
        return `${url}?${searchParams.toString()}`;
    }
    /**
     * Apply request interceptors
     */
    applyRequestInterceptors(config) {
        return __awaiter(this, void 0, void 0, function* () {
            let modifiedConfig = Object.assign({}, config);
            for (const interceptor of this.interceptors) {
                if (interceptor.request) {
                    modifiedConfig = yield interceptor.request(modifiedConfig);
                }
            }
            return modifiedConfig;
        });
    }
    /**
     * Apply response interceptors
     */
    applyResponseInterceptors(response) {
        return __awaiter(this, void 0, void 0, function* () {
            let modifiedResponse = response;
            for (const interceptor of this.interceptors) {
                if (interceptor.response) {
                    modifiedResponse = yield interceptor.response(modifiedResponse);
                }
            }
            return modifiedResponse;
        });
    }
    /**
     * Apply error interceptors
     */
    applyErrorInterceptors(error) {
        return __awaiter(this, void 0, void 0, function* () {
            let modifiedError = error;
            for (const interceptor of this.interceptors) {
                if (interceptor.error) {
                    modifiedError = yield interceptor.error(modifiedError);
                }
            }
            return modifiedError;
        });
    }
    /**
     * Log request in development
     */
    logRequest(method, url, options) {
        if (!api_1.API_CONFIG.ENABLE_LOGGING)
            return;
        /* eslint-disable no-console */
        console.group(`🌐 API Request: ${method} ${url}`);
        if (options === null || options === void 0 ? void 0 : options.body) {
            try {
                const parsed = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
                console.log('Body:', parsed);
            }
            catch (_a) {
                console.log('Body: [Invalid JSON - unable to parse]');
            }
        }
        if (options === null || options === void 0 ? void 0 : options.params) {
            console.log('Params:', options.params);
        }
        console.groupEnd();
        /* eslint-enable no-console */
    }
    /**
     * Log response in development
     */
    logResponse(method, url, response, data) {
        if (!api_1.API_CONFIG.ENABLE_LOGGING)
            return;
        /* eslint-disable no-console */
        const emoji = response.ok ? '✅' : '❌';
        console.group(`${emoji} API Response: ${method} ${url} (${response.status})`);
        if (data) {
            console.log('Data:', data);
        }
        console.groupEnd();
        /* eslint-enable no-console */
    }
    /**
     * Perform fetch with timeout and secure defaults
     */
    fetchWithTimeout(url_1, options_1) {
        return __awaiter(this, arguments, void 0, function* (url, options, timeout = api_1.API_CONFIG.TIMEOUT) {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);
            try {
                const response = yield fetch(url, Object.assign(Object.assign({}, options), { mode: 'cors', credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer', signal: controller.signal }));
                clearTimeout(timeoutId);
                return response;
            }
            catch (error) {
                clearTimeout(timeoutId);
                if (error instanceof Error && error.name === 'AbortError') {
                    throw this.createApiError('Request timeout', 408);
                }
                if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
                    throw this.createApiError('Network error. Please check your connection.', 0);
                }
                throw error;
            }
        });
    }
    /**
     * Create standardized API error
     */
    createApiError(message, status, code, details) {
        return {
            message,
            status,
            code,
            details,
        };
    }
    /**
     * Handle API error response
     *
     * SECURITY: Only expose sanitized error messages to clients.
     * Never include server error details (stack traces, internal paths, etc.)
     * as this aids attackers in reconnaissance.
     */
    handleErrorResponse(response) {
        return __awaiter(this, void 0, void 0, function* () {
            let errorMessage = 'An error occurred';
            let errorCode;
            // Preserve full body for 4xx business errors (e.g. snap 400 with invalid student IDs).
            // SECURITY: Only preserved for client errors (4xx), never for 5xx.
            let errorDetails;
            try {
                const errorData = yield response.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
                errorCode = errorData.code;
                if (response.status >= 400 && response.status < 500) {
                    errorDetails = errorData;
                }
            }
            catch (_a) {
                // If response is not JSON, use status text
                errorMessage = response.statusText || errorMessage;
            }
            // Create appropriate error based on status code
            // SECURITY: Use generic messages for server errors to prevent information disclosure
            if (response.status === 401) {
                throw this.createApiError('Unauthorized: Invalid or expired token', 401, 'UNAUTHORIZED');
            }
            else if (response.status === 403) {
                throw this.createApiError('Forbidden: Insufficient permissions', 403, 'FORBIDDEN');
            }
            else if (response.status === 404) {
                throw this.createApiError('Resource not found', 404, 'NOT_FOUND');
            }
            else if (response.status >= 500) {
                // NEVER expose server error details - aids reconnaissance
                throw this.createApiError('Server error. Please try again later.', response.status, 'SERVER_ERROR');
            }
            else {
                throw this.createApiError(errorMessage, response.status, errorCode || 'API_ERROR', errorDetails);
            }
        });
    }
    /**
     * Perform HTTP request with retry logic
     */
    requestWithRetry(method_1, endpoint_1) {
        return __awaiter(this, arguments, void 0, function* (method, endpoint, options = {}, attempt = 1) {
            var _a;
            const { params, retry = true, requiresAuth = true } = options, fetchOptions = __rest(options, ["params", "retry", "requiresAuth"]);
            // Build URL
            const url = this.buildUrl(endpoint, params);
            // Prepare headers
            const headers = Object.assign({ 'Content-Type': 'application/json' }, fetchOptions.headers);
            // Add authorization header if required.
            // tokenOverride (page-scoped, e.g. Salesforce iframe) takes priority over the
            // normal session token getter.
            if (requiresAuth) {
                const token = (_a = this.tokenOverride) !== null && _a !== void 0 ? _a : (this.getToken ? this.getToken() : null);
                if (token) {
                    headers['Authorization'] = token;
                }
            }
            // Prepare request config
            let requestConfig = Object.assign({ method,
                headers }, fetchOptions);
            // Apply request interceptors
            requestConfig = yield this.applyRequestInterceptors(requestConfig);
            // Log request
            this.logRequest(method, url, options);
            try {
                // Perform fetch
                let response = yield this.fetchWithTimeout(url, requestConfig);
                // Apply response interceptors
                response = yield this.applyResponseInterceptors(response);
                // Handle 401 Unauthorized - try token refresh.
                // Skip when tokenOverride is active (iframe): an invalid iframe token must NOT
                // trigger a session-wide refresh or logout.
                if (response.status === 401 && this.refreshToken && attempt === 1 && !this.tokenOverride) {
                    const newToken = yield this.refreshToken();
                    if (newToken) {
                        // Retry with new token
                        return this.requestWithRetry(method, endpoint, options, attempt + 1);
                    }
                }
                // Handle error responses
                if (!response.ok) {
                    yield this.handleErrorResponse(response);
                }
                // Parse response
                const data = yield response.json();
                // Log response
                this.logResponse(method, url, response, data);
                return data;
            }
            catch (error) {
                // Apply error interceptors
                const processedError = yield this.applyErrorInterceptors(error);
                // Retry logic for network errors
                if (retry && attempt < api_1.API_CONFIG.MAX_RETRIES) {
                    const isRetryableError = (error instanceof Error && error.message.includes('Network error')) ||
                        error.status === 0 ||
                        error.status >= 500;
                    if (isRetryableError) {
                        // Exponential backoff
                        const delay = api_1.API_CONFIG.RETRY_DELAY * Math.pow(2, attempt - 1);
                        yield new Promise(resolve => setTimeout(resolve, delay));
                        return this.requestWithRetry(method, endpoint, options, attempt + 1);
                    }
                }
                throw processedError;
            }
        });
    }
    /**
     * GET request
     */
    get(endpoint, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.requestWithRetry('GET', endpoint, options);
        });
    }
    /**
     * POST request
     */
    post(endpoint, body, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.requestWithRetry('POST', endpoint, Object.assign(Object.assign({}, options), { body: body ? JSON.stringify(body) : undefined }));
        });
    }
    /**
     * PUT request
     */
    put(endpoint, body, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.requestWithRetry('PUT', endpoint, Object.assign(Object.assign({}, options), { body: body ? JSON.stringify(body) : undefined }));
        });
    }
    /**
     * PATCH request
     */
    patch(endpoint, body, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.requestWithRetry('PATCH', endpoint, Object.assign(Object.assign({}, options), { body: body ? JSON.stringify(body) : undefined }));
        });
    }
    /**
     * DELETE request
     */
    delete(endpoint, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.requestWithRetry('DELETE', endpoint, options);
        });
    }
    /**
     * POST request that returns a Blob (e.g. PDF export).
     * Uses the same auth header injection as requestWithRetry but streams the
     * response body as a Blob instead of parsing JSON.
     */
    postBlob(endpoint_1, body_1) {
        return __awaiter(this, arguments, void 0, function* (endpoint, body, options = {}) {
            var _a;
            const url = this.buildUrl(endpoint, options.params);
            const headers = Object.assign({ 'Content-Type': 'application/json' }, options.headers);
            if (options.requiresAuth !== false) {
                const token = (_a = this.tokenOverride) !== null && _a !== void 0 ? _a : (this.getToken ? this.getToken() : null);
                if (token) {
                    headers['Authorization'] = token;
                }
            }
            const response = yield this.fetchWithTimeout(url, {
                method: 'POST',
                headers,
                body: body ? JSON.stringify(body) : undefined,
            });
            if (!response.ok) {
                yield this.handleErrorResponse(response);
            }
            return response.blob();
        });
    }
}
exports.ApiClient = ApiClient;
// Export singleton instance
exports.apiClient = new ApiClient();
